import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Broadcast, Megaphone, UserFocus, ArrowRight, FileText, ChartLineUp,
  CurrencyDollar, UsersThree, User as UserIcon, Storefront, Notebook, Sparkle,
  EnvelopeSimple, PaperPlaneTilt, EnvelopeOpen, ChatCircle, CalendarCheck, Prohibit, Warning, CheckCircle,
} from "@phosphor-icons/react";

function fmtMoney(n) {
  const v = Number(n || 0);
  if (!v) return "—";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}k`;
  return `$${Math.round(v)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return "—"; }
}

export default function MarketingHub() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, mp] = await Promise.all([
        api.get("/marketing/overview").catch(() => ({ data: null })),
        api.get("/marketing/performance", { params: { days: 30 } }).catch(() => ({ data: null })),
      ]);
      setData(ov.data);
      setPerf(mp.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const [email, setEmail] = useState({ loading: true, data: null, error: false });
  useEffect(() => {
    let alive = true;
    setEmail({ loading: true, data: null, error: false });
    api.get("/marketing/email", { params: { days: 30 } })
      .then((r) => { if (alive) setEmail({ loading: false, data: r.data, error: false }); })
      .catch(() => { if (alive) setEmail({ loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, [currentOrg?.org_id]);

  const [li, setLi] = useState({ loading: true, data: null, error: false });
  const loadLi = useCallback(async () => {
    try {
      const r = await api.get("/marketing/linkedin", { params: { days: 30 } });
      setLi({ loading: false, data: r.data, error: false });
    } catch {
      setLi({ loading: false, data: null, error: true });
    }
  }, []);
  useEffect(() => {
    setLi({ loading: true, data: null, error: false });
    loadLi();
  }, [loadLi, currentOrg?.org_id]);

  const t = data?.totals || {};
  const active = data?.active_campaigns || [];
  const recent = data?.recent_content || [];
  const noCampaigns = !loading && (t.all_campaigns || 0) === 0;

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="marketing-hub-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Broadcast weight="duotone" className="h-3 w-3" /> Marketing
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Overview</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Everything already live in this workspace — active campaigns, the content flowing
            through them, plus quick access to personas and channels.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/campaigns" data-testid="marketing-hub-new-campaign"
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200">
            <Megaphone weight="fill" className="h-4 w-4" /> Campaigns
            <ArrowRight weight="bold" className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary">Loading marketing overview…</div>
      ) : noCampaigns ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
          data-testid="marketing-hub-empty">
          <Megaphone weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No campaigns yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            Once you launch your first campaign, this page will surface its performance, linked
            content, and attribution alongside everything else marketing.
          </p>
          <Link to="/campaigns" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200">
            <Megaphone weight="fill" className="h-4 w-4" /> Create a campaign
            <ArrowRight weight="bold" className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <>
          {/* Summary strip */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="marketing-hub-summary">
            <StatCard label="Active campaigns" value={t.active_campaigns || 0} sub={`${t.all_campaigns || 0} total`} Icon={Megaphone} tone="text-emerald-300" testid="marketing-stat-active" />
            <StatCard label="Leads attributed" value={t.leads || 0} sub="across all campaigns" Icon={UsersThree} tone="text-blue-300" testid="marketing-stat-leads" />
            <StatCard label="Customers attributed" value={t.customers || 0} sub="acquisition_campaign_id" Icon={UserIcon} tone="text-fuchsia-300" testid="marketing-stat-customers" />
            <StatCard label="Revenue attributed" value={fmtMoney(t.revenue)} sub="sum of revenue events" Icon={CurrencyDollar} tone="text-emerald-200" testid="marketing-stat-revenue" mono />
          </div>

          {/* Active Campaigns + Recent Content grid */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section
              data-testid="marketing-hub-active-campaigns"
              className="lg:col-span-2 rounded-md border border-white/10 bg-graphite-900 p-6"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                    <ChartLineUp weight="duotone" className="h-3 w-3" /> Live
                  </div>
                  <h2 className="mt-0.5 font-display text-lg tracking-tight">Active campaigns</h2>
                </div>
                <Link to="/campaigns" className="text-[11px] text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1">
                  All campaigns <ArrowRight weight="bold" className="h-3 w-3" />
                </Link>
              </div>
              {active.length === 0 ? (
                <div className="mt-5 text-sm text-ink-secondary" data-testid="marketing-hub-active-empty">
                  No campaigns are currently active. Once one is set to running or has a start date,
                  it'll show up here with attributed leads, customers, and revenue.
                </div>
              ) : (
                <ul className="mt-4 space-y-2" data-testid="marketing-hub-active-list">
                  {active.map((c) => (
                    <li
                      key={c.campaign_id}
                      data-testid={`marketing-campaign-${c.campaign_id}`}
                      className="rounded-md border border-white/10 bg-graphite-950/40 px-4 py-3 hover:bg-graphite-950/60 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <Link to="/campaigns" className="text-sm text-ink-primary truncate hover:text-white block">
                            {c.name || "(unnamed campaign)"}
                          </Link>
                          <div className="mt-0.5 text-[11px] text-ink-secondary flex items-center gap-2 flex-wrap">
                            {c.status && <span className="inline-flex items-center rounded-sm border border-white/10 bg-graphite-950 px-1.5 py-0.5 uppercase tracking-widest text-[9px]">{c.status}</span>}
                            {(c.start_date || c.end_date) && <span className="tabular-nums">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>}
                          </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-4 text-xs tabular-nums">
                          <MetricPill label="Leads" value={c.perf_summary?.leads_count || 0} />
                          <MetricPill label="Customers" value={c.perf_summary?.customers_count || 0} />
                          <MetricPill label="Revenue" value={fmtMoney(c.perf_summary?.revenue_total)} accent />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Personas & Channels quick-access */}
            <section
              data-testid="marketing-hub-quick-access"
              className="rounded-md border border-white/10 bg-graphite-900 p-6 flex flex-col gap-4"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Building blocks</div>
                <h2 className="mt-0.5 font-display text-lg tracking-tight">Personas & Channels</h2>
              </div>
              <QuickTile to="/personas" Icon={UserFocus} label="Personas" count={t.personas || 0}
                blurb="Audience profiles driving copy + strategy." testid="marketing-hub-personas-tile" />
              <QuickTile to="/campaigns" Icon={Broadcast} label="Channels" count={t.channels || 0}
                blurb="Where campaigns run. Managed alongside campaigns." testid="marketing-hub-channels-tile" />
              <QuickTile to="/landing-pages" Icon={Storefront} label="Landing pages"
                blurb="Public pages that capture attention into leads." testid="marketing-hub-landing-tile" />
              <QuickTile to="/forms" Icon={Notebook} label="Forms"
                blurb="Public forms that feed leads into your pipeline." testid="marketing-hub-forms-tile" />
            </section>

            {/* Recent Content — full width below */}
            <section
              data-testid="marketing-hub-recent-content"
              className="lg:col-span-3 rounded-md border border-white/10 bg-graphite-900 p-6"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                    <FileText weight="duotone" className="h-3 w-3" /> Recent
                  </div>
                  <h2 className="mt-0.5 font-display text-lg tracking-tight">Content items</h2>
                </div>
                <Link to="/campaigns" className="text-[11px] text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1">
                  Open in Campaigns <ArrowRight weight="bold" className="h-3 w-3" />
                </Link>
              </div>
              {recent.length === 0 ? (
                <div className="mt-5 text-sm text-ink-secondary" data-testid="marketing-hub-content-empty">
                  No content items yet. Add pillars and draft content from any campaign's detail view.
                </div>
              ) : (
                <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="marketing-hub-content-list">
                  {recent.map((r) => (
                    <li
                      key={r.content_id}
                      data-testid={`marketing-content-${r.content_id}`}
                      className="rounded-md border border-white/10 bg-graphite-950/40 p-3"
                    >
                      <div className="text-sm text-ink-primary truncate">{r.title || "(untitled)"}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-2">
                        {r.status && <span className="border border-white/10 px-1.5 py-0.5 rounded-sm">{r.status}</span>}
                        <span className="normal-case tracking-normal text-[11px] truncate">
                          {r.campaign_name || "—"}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Marketing Performance section (Phase 2 — reads marketing_metrics) */}
          <MarketingPerformanceSection perf={perf} />
        </>
      )}

      {!loading && <EmailOutreachSection email={email} />}
      {!loading && <LinkedInOutreachSection li={li} onSaved={loadLi} orgId={currentOrg?.org_id} />}
    </div>
  );
}

const eNum = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0";
};
const ePct = (f) => {
  const v = Number(f);
  return f === null || f === undefined || !Number.isFinite(v) ? "—" : `${(v * 100).toFixed(1)}%`;
};
const E_COUNTS = [
  ["sent", "Sent"], ["delivered", "Delivered"], ["opens", "Opens"], ["replies", "Replies"],
  ["interested", "Interested"], ["meetings_booked", "Meetings"], ["unsubscribes", "Unsubs"], ["bounces", "Bounces"],
];
const countCols = (keys) => E_COUNTS.filter(([k]) => keys.includes(k))
  .map(([k, label]) => ({ label, right: true, get: (r) => eNum(r[k]) }));
const ALL_COUNTS = E_COUNTS.map(([k]) => k);

function EmailShell({ children, testid }) {
  return (
    <section data-testid={testid} className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-8 text-center">
      <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center justify-center gap-1.5">
        <EnvelopeSimple weight="duotone" className="h-3 w-3" /> Email outreach
      </div>
      {children}
    </section>
  );
}

function EmailOutreachSection({ email }) {
  if (email.loading) return null;
  if (email.error) {
    return (
      <EmailShell testid="email-outreach-section">
        <div className="mt-3 text-sm text-ink-secondary" data-testid="email-outreach-error">Email data unavailable</div>
      </EmailShell>
    );
  }
  const d = email.data;
  if (!d || !d.has_data || !d.totals) {
    return (
      <EmailShell testid="email-outreach-section">
        <div className="mt-3 font-display text-xl tracking-tight" data-testid="email-outreach-empty">No Saleshandy email data yet</div>
        <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
          Email stats appear here once the daily Saleshandy feed is connected.
        </p>
      </EmailShell>
    );
  }
  const t = d.totals;
  const kpis = [
    ["sent", "Sent", PaperPlaneTilt, "text-blue-300", null],
    ["delivered", "Delivered", CheckCircle, "text-emerald-300", ["delivery_rate", "delivery rate"]],
    ["opens", "Opens", EnvelopeOpen, "text-blue-300", ["open_rate", "open rate"]],
    ["replies", "Replies", ChatCircle, "text-fuchsia-300", ["reply_rate", "reply rate"]],
    ["interested", "Interested", Sparkle, "text-emerald-200", ["interested_rate", "interested rate"]],
    ["meetings_booked", "Meetings", CalendarCheck, "text-emerald-200", null],
    ["unsubscribes", "Unsubscribes", Prohibit, "text-amber-300", ["unsubscribe_rate", "unsubscribe rate"]],
    ["bounces", "Bounces", Warning, "text-amber-300", ["bounce_rate", "bounce rate"]],
  ];
  const stepLabel = (r) => `Step ${r.step_number}`;
  return (
    <section data-testid="email-outreach-section" className="mt-6 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          <EnvelopeSimple weight="duotone" className="h-3 w-3" /> Email outreach
        </div>
        <h2 className="mt-0.5 font-display text-lg tracking-tight">Last {d.days} days · from {d.row_count} rows</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="email-outreach-kpis">
        {kpis.map(([k, label, Icon, tone, rate]) => (
          <StatCard key={k} label={label} value={eNum(t[k])} Icon={Icon} tone={tone} mono
            sub={rate ? `${ePct(t[rate[0]])} ${rate[1]}` : null} testid={`email-kpi-${k}`} />
        ))}
      </div>
      <EmailTable testid="email-table-by-day" title="By day" rows={d.by_day} rowKey={(r, i) => `${r.date || "none"}-${i}`}
        cols={[{ label: "Date", get: (r) => r.date }, ...countCols(ALL_COUNTS)]} />
      <EmailTable testid="email-table-by-campaign" title="By campaign" rows={d.by_campaign}
        rowKey={(r, i) => `${r.campaign_name || "none"}-${i}`}
        cols={[{ label: "Campaign", get: (r) => r.campaign_name || "—" }, ...countCols(ALL_COUNTS),
          { label: "Reply %", right: true, get: (r) => ePct(r.reply_rate) },
          { label: "Interested %", right: true, get: (r) => ePct(r.interested_rate) }]} />
      <EmailTable testid="email-table-by-step" title="By email step" rows={d.by_step}
        rowKey={(r, i) => `${r.campaign_name || "none"}-${r.step_number}-${i}`}
        cols={[{ label: "Campaign", get: (r) => r.campaign_name || "—" }, { label: "Step", get: stepLabel },
          ...countCols(["sent", "delivered", "opens", "replies", "bounces"]),
          { label: "Open %", right: true, get: (r) => ePct(r.open_rate) },
          { label: "Reply %", right: true, get: (r) => ePct(r.reply_rate) }]} />
    </section>
  );
}

function EmailTable({ testid, title, rows, cols, rowKey }) {
  return (
    <div className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid={testid}>
      <h3 className="font-display text-base tracking-tight">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <div className="mt-3 text-sm text-ink-secondary" data-testid={`${testid}-empty`}>No rows in this period.</div>
      ) : (
        <div className="mt-3 overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                {cols.map((c) => (
                  <th key={c.label} className={`px-4 py-2 font-medium ${c.right ? "text-right" : ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r, idx) => (
                <tr key={rowKey(r, idx)} data-testid={`${testid}-row-${rowKey(r, idx)}`} className="hover:bg-white/[0.02]">
                  {cols.map((c, i) => (
                    <td key={c.label} className={`px-4 py-2 ${c.right ? "text-right tabular-nums text-ink-secondary" : i === 0 ? "text-ink-primary" : "text-ink-secondary"}`}>
                      {c.get(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MarketingPerformanceSection({ perf }) {
  if (!perf) return null;
  const fmtNum = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmtMoney2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  if (!perf.has_data) {
    return (
      <section
        data-testid="marketing-performance-section"
        className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-8 text-center"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center justify-center gap-1.5">
          <ChartLineUp weight="duotone" className="h-3 w-3" /> Marketing performance
        </div>
        <div className="mt-3 font-display text-xl tracking-tight" data-testid="marketing-performance-empty">
          No marketing data yet
        </div>
        <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
          Connect Metricool via the Inbound API (see the API key dialog in Platform Admin) to see reach,
          impressions, engagement, spend and CPL rolled up here.
        </p>
      </section>
    );
  }
  const t = perf.totals || {};
  return (
    <section data-testid="marketing-performance-section" className="mt-6 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          <ChartLineUp weight="duotone" className="h-3 w-3" /> Marketing performance
        </div>
        <h2 className="mt-0.5 font-display text-lg tracking-tight">Last {perf.days} days · from {perf.row_count} rows</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="marketing-performance-kpis">
        <StatCard label="Reach" value={fmtNum(t.reach)} Icon={UsersThree} tone="text-blue-300" testid="mperf-reach" mono />
        <StatCard label="Impressions" value={fmtNum(t.impressions)} Icon={ChartLineUp} tone="text-blue-300" testid="mperf-impressions" mono />
        <StatCard label="Engagement" value={fmtNum(t.engagement)} Icon={Sparkle} tone="text-fuchsia-300" testid="mperf-engagement" mono />
        <StatCard label="Spend" value={fmtMoney2(t.spend)} Icon={CurrencyDollar} tone="text-emerald-200" testid="mperf-spend" mono />
        <StatCard label="CPL" value={t.cpl === null ? "—" : fmtMoney2(t.cpl)} sub={t.cpl === null ? "no leads yet" : "spend ÷ leads"} Icon={CurrencyDollar} tone="text-amber-300" testid="mperf-cpl" mono />
        <StatCard label="Conversion rate" value={t.conversion_rate === null ? "—" : `${t.conversion_rate}%`} sub={t.conversion_rate === null ? "no clicks yet" : "conversions ÷ clicks"} Icon={ChartLineUp} tone="text-emerald-300" testid="mperf-conv" mono />
      </div>
      <div className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="marketing-performance-platforms">
        <h3 className="font-display text-base tracking-tight">By platform</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                <th className="px-4 py-2 font-medium">Platform</th>
                <th className="px-4 py-2 font-medium text-right">Reach</th>
                <th className="px-4 py-2 font-medium text-right">Impressions</th>
                <th className="px-4 py-2 font-medium text-right">Engagement</th>
                <th className="px-4 py-2 font-medium text-right">Clicks</th>
                <th className="px-4 py-2 font-medium text-right">Spend</th>
                <th className="px-4 py-2 font-medium text-right">Leads</th>
                <th className="px-4 py-2 font-medium text-right">CPL</th>
                <th className="px-4 py-2 font-medium text-right">Conv %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {perf.by_platform.map((r) => (
                <tr key={r.platform} data-testid={`mperf-platform-${r.platform}`} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-2 text-ink-primary">{r.platform}</td>
                  <td className="px-4 py-2 text-right text-ink-secondary tabular-nums">{fmtNum(r.reach)}</td>
                  <td className="px-4 py-2 text-right text-ink-secondary tabular-nums">{fmtNum(r.impressions)}</td>
                  <td className="px-4 py-2 text-right text-ink-secondary tabular-nums">{fmtNum(r.engagement)}</td>
                  <td className="px-4 py-2 text-right text-ink-secondary tabular-nums">{fmtNum(r.clicks)}</td>
                  <td className="px-4 py-2 text-right text-ink-primary tabular-nums">{fmtMoney2(r.spend)}</td>
                  <td className="px-4 py-2 text-right text-ink-secondary tabular-nums">{fmtNum(r.leads)}</td>
                  <td className="px-4 py-2 text-right text-amber-200/90 tabular-nums">{r.cpl === null ? "—" : fmtMoney2(r.cpl)}</td>
                  <td className="px-4 py-2 text-right text-emerald-200/90 tabular-nums">{r.conversion_rate === null ? "—" : `${r.conversion_rate}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, sub, Icon, tone, testid, mono }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/10 bg-graphite-900 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{label}</div>
        <Icon weight="duotone" className={`h-4 w-4 ${tone || "text-white"}`} />
      </div>
      <div className={`mt-2 font-display text-2xl tracking-tight ${mono ? "tabular-nums" : ""}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-ink-secondary/70">{sub}</div>}
    </div>
  );
}

function MetricPill({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-ink-secondary/70">{label}</div>
      <div className={`text-sm tabular-nums ${accent ? "text-emerald-200" : "text-ink-primary"}`}>{value}</div>
    </div>
  );
}

function QuickTile({ to, Icon, label, count, blurb, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-md border border-white/10 bg-graphite-950/40 p-3 hover:bg-graphite-950/70 transition-colors flex items-start gap-3"
    >
      <div className="h-8 w-8 rounded-md bg-graphite-950 border border-white/10 grid place-items-center shrink-0">
        <Icon weight="duotone" className="h-4 w-4 text-ink-primary/90" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm text-ink-primary">{label}</div>
          {count !== undefined && (
            <span className="text-[10px] uppercase tracking-widest text-ink-secondary border border-white/10 px-1.5 py-0.5 rounded-sm tabular-nums">
              {count}
            </span>
          )}
          <ArrowRight weight="bold" className="ml-auto h-3 w-3 text-ink-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="mt-0.5 text-[11px] text-ink-secondary">{blurb}</div>
      </div>
    </Link>
  );
}


const LI_FIELDS = [
  ["invites_sent", "Invites Sent"], ["accepted", "Accepted"], ["dms_sent", "DMs Sent"],
  ["replies", "Replies"], ["interested", "Interested"], ["meetings_booked", "Meetings Booked"],
];
const liInput = "w-full rounded-md bg-graphite-950 border border-white/10 px-3 py-2 text-sm text-ink-primary focus:outline-none focus:border-white/30";
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const errDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
};

function LinkedInOutreachSection({ li, onSaved, orgId }) {
  if (li.loading) return null;
  const d = li.data;
  const hasData = !!(d && d.has_data && d.totals);
  let body;
  if (li.error) {
    body = <div className="text-sm text-ink-secondary" data-testid="linkedin-outreach-error">LinkedIn data unavailable</div>;
  } else if (!hasData) {
    body = (
      <div className="rounded-md border border-white/10 bg-graphite-900 p-8 text-center">
        <div className="font-display text-xl tracking-tight" data-testid="linkedin-outreach-empty">No LinkedIn outreach logged yet</div>
        <p className="mt-2 text-sm text-ink-secondary">Add today's activity below to start tracking.</p>
      </div>
    );
  } else {
    const t = d.totals;
    const rateFor = { accepted: ["acceptance_rate", "acceptance"], replies: ["reply_rate", "reply rate"], interested: ["interested_rate", "of replies"] };
    const counts = LI_FIELDS.map(([k, label]) => ({ label, right: true, get: (r) => eNum(r[k]) }));
    body = (
      <>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="linkedin-outreach-kpis">
          {LI_FIELDS.map(([k, label]) => (
            <StatCard key={k} label={label} value={eNum(t[k])} Icon={UsersThree} tone="text-blue-300" mono
              sub={rateFor[k] ? `${ePct(t[rateFor[k][0]])} ${rateFor[k][1]}` : null} testid={`linkedin-kpi-${k}`} />
          ))}
        </div>
        <EmailTable testid="linkedin-table-by-day" title="By day" rows={d.by_day}
          rowKey={(r, i) => `${r.date || "none"}-${i}`} cols={[{ label: "Date", get: (r) => r.date }, ...counts]} />
        <EmailTable testid="linkedin-table-by-campaign" title="By campaign" rows={d.by_campaign}
          rowKey={(r, i) => `${r.campaign_name || "none"}-${i}`}
          cols={[{ label: "Campaign", get: (r) => r.campaign_name || "—" }, ...counts,
            { label: "Accept %", right: true, get: (r) => ePct(r.acceptance_rate) },
            { label: "Reply %", right: true, get: (r) => ePct(r.reply_rate) }]} />
      </>
    );
  }
  const entries = d?.entries || [];
  const campaigns = [...new Set(entries.map((e) => e.campaign_name).filter(Boolean))];
  return (
    <section data-testid="linkedin-outreach-section" className="mt-6 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          <UsersThree weight="duotone" className="h-3 w-3" /> LinkedIn outreach
        </div>
        {hasData && <h2 className="mt-0.5 font-display text-lg tracking-tight">Last {d.days} days · from {d.row_count} entries</h2>}
      </div>
      {body}
      <LiActivityForm key={`act-${orgId}`} entries={entries} campaigns={campaigns} onSaved={onSaved} />
      <LiProspectForm key={`pros-${orgId}`} campaigns={campaigns} />
    </section>
  );
}

function LiActivityForm({ entries, campaigns, onSaved }) {
  const blank = Object.fromEntries(LI_FIELDS.map(([k]) => [k, ""]));
  const [day, setDay] = useState(todayIso());
  const [campaign, setCampaign] = useState("");
  const [vals, setVals] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const match = entries.find((e) => e.date === day && (e.campaign_name || "").toLowerCase() === campaign.trim().toLowerCase() && campaign.trim());
  useEffect(() => {
    if (match) {
      setVals(Object.fromEntries(LI_FIELDS.map(([k]) => [k, String(match[k] ?? 0)])));
    }
  }, [match]);
  const save = async () => {
    setMsg(null);
    if (!campaign.trim()) { setMsg({ ok: false, text: "Campaign is required" }); return; }
    setSaving(true);
    try {
      const body = { date: day, campaign_name: campaign.trim() };
      LI_FIELDS.forEach(([k]) => { const n = parseInt(vals[k], 10); body[k] = Number.isFinite(n) && n > 0 ? n : 0; });
      const r = await api.put("/marketing/linkedin/entry", body);
      setMsg({ ok: true, text: r.data?.created ? "Saved" : "Updated" });
      await onSaved();
    } catch (e) {
      setMsg({ ok: false, text: errDetail(e, "Could not save") });
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="linkedin-activity-form">
      <h3 className="font-display text-base tracking-tight">Add / Edit Daily Activity</h3>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input type="date" className={liInput} value={day} onChange={(e) => setDay(e.target.value)} data-testid="linkedin-activity-date" />
        <input className={liInput} placeholder="Campaign" list="linkedin-campaigns" value={campaign} maxLength={120}
          onChange={(e) => setCampaign(e.target.value)} data-testid="linkedin-activity-campaign" />
        <datalist id="linkedin-campaigns">{campaigns.map((c) => <option key={c} value={c} />)}</datalist>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {LI_FIELDS.map(([k, label]) => (
          <label key={k} className="text-[10px] uppercase tracking-widest text-ink-secondary">
            {label}
            <input type="number" min="0" step="1" className={`${liInput} mt-1`} value={vals[k]}
              onChange={(e) => setVals({ ...vals, [k]: e.target.value })} data-testid={`linkedin-activity-${k}`} />
          </label>
        ))}
      </div>
      {match && <div className="mt-3 text-xs text-amber-200/90" data-testid="linkedin-activity-editing">Editing existing entry — saving replaces these values</div>}
      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={save} disabled={saving} data-testid="linkedin-activity-save"
          className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50">
          {saving ? "Saving…" : match ? "Update" : "Save"}
        </button>
        {msg && <span className={`text-sm ${msg.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="linkedin-activity-msg">{msg.text}</span>}
      </div>
    </div>
  );
}

function LiProspectForm({ campaigns }) {
  const empty = { name: "", company: "", email: "", linkedin_url: "", campaign: "", notes: "" };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [pipe, setPipe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !pipe) {
      try {
        const { data } = await api.get("/pipelines");
        const p = (data || [])[0];
        if (p && p.stages?.length) setPipe({ pipeline_id: p.pipeline_id, stage_id: p.stages[0].stage_id });
        else setMsg({ ok: false, text: "No pipeline found — create one in Leads first" });
      } catch { setMsg({ ok: false, text: "Could not load pipelines" }); }
    }
  };
  const submit = async () => {
    setMsg(null);
    if (!f.name.trim()) { setMsg({ ok: false, text: "Name is required" }); return; }
    if (!f.email.trim() && !f.linkedin_url.trim()) { setMsg({ ok: false, text: "Add an email or a LinkedIn profile URL" }); return; }
    if (!pipe) { setMsg({ ok: false, text: "Pipeline not loaded yet" }); return; }
    setSaving(true);
    const notes = [f.campaign.trim() && `Campaign: ${f.campaign.trim()}`, f.linkedin_url.trim() && `LinkedIn: ${f.linkedin_url.trim()}`, f.notes.trim()]
      .filter(Boolean).join("\n");
    try {
      await api.post("/leads", {
        name: f.name.trim(), company: f.company.trim(), email: f.email.trim(),
        linkedin_url: f.linkedin_url.trim() || null, source: "LinkedIn", notes, ...pipe,
      });
      setMsg({ ok: true, text: "Added to CRM Leads" });
      setF(empty);
    } catch (e) {
      if (e?.response?.status === 409) {
        const n = e.response.data?.detail?.name;
        setMsg({ ok: false, text: n ? `Already in your CRM (${n})` : "Already in your CRM" });
      } else setMsg({ ok: false, text: errDetail(e, "Could not add lead") });
    } finally { setSaving(false); }
  };
  const field = (k, ph, extra = {}) => (
    <input className={liInput} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
      data-testid={`linkedin-prospect-${k}`} {...extra} />
  );
  return (
    <div className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="linkedin-prospect-form">
      <button type="button" onClick={toggle} className="font-display text-base tracking-tight flex items-center gap-2"
        data-testid="linkedin-prospect-toggle">
        Add Interested Prospect <ArrowRight weight="bold" className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {field("name", "Name *")}
            {field("company", "Company")}
            {field("email", "Email", { type: "email" })}
            {field("linkedin_url", "LinkedIn profile URL")}
            {field("campaign", "Campaign", { list: "linkedin-prospect-campaigns" })}
            {field("notes", "Notes")}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button type="button" onClick={submit} disabled={saving} data-testid="linkedin-prospect-submit"
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50">
              {saving ? "Adding…" : "Add to CRM"}
            </button>
            {msg && <span className={`text-sm ${msg.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="linkedin-prospect-msg">{msg.text}</span>}
          </div>
        </>
      )}
      {!open && msg && <div className="mt-2 text-sm text-red-300">{msg.text}</div>}
      <datalist id="linkedin-prospect-campaigns">{campaigns.map((c) => <option key={c} value={c} />)}</datalist>
    </div>
  );
}
